import {jsx, ParamComponent} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {DropMenuEl} from 'common/components/drop-menu';
import {ItemsMenuEl} from 'common/components/items-menu';
import {SelectTheme} from 'common/components/select/constants';
import {TagInputEl} from 'common/components/tag-input';
import {TagItem} from 'common/components/tag-input/tag-item';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Section} from 'common/components/items-menu/types';

import css from './index.inline.styl';

export const enum ElmRefs {
  TagInputWrapper = `tagInputWrapper`,
  TagInput = `tagInput`,
  DropMenu = `dropMenu`,
}

interface Params {
  tags: TagItem[];
  selectOptions: Section[];
  disabled: boolean;
  placeholder: string;
  menuSections: Section[];
  sectionsFromInputFn: (input) => Section[];

  cta: {
    text: string;
    disabled: boolean;
    onClick: () => void;
    icon: string;
  };

  selectedInputOption: any;
  itemMenuKeydownProxy: any;

  picker: {
    handleInputChange: (ev) => void;
    handleLevelSelectChange: (ev) => void;
    handleItemClick: (ev) => void;
    handleInputFocus: () => void;
    handlePopoverChange: (ev) => void;
    handleMpTagInputInsert: (ev) => void;
    handleDropMenuInsert: (ev) => void;
    handleWrapperInsert: (ev) => void;
  };

  searchText: string;
  showPopover: boolean;
}

export class UserSearchTagInput extends ParamComponent<Params, EmptyObj> {
  get config() {
    return {
      css,
      useShadowDom: true,
      params: {
        tags: Array,
        selectOptions: Array,
        disabled: Boolean,
        placeholder: String,
        menuSections: Array,
        itemMenuKeydownProxy: Object,

        cta: Object,

        selectedInputOption: String,
        sectionsFromInputFn: Function,

        picker: Object,

        searchText: String,
        showPopover: Boolean,
      },
      template: () => {
        return (
          <div
            sel='.picker-wrapper'
            key='wrapper'
            hook={{
              insert: this.params.picker.handleWrapperInsert,
            }}
          >
            <label attrs={{for: `picker`}}>Invite Teammates</label>
            <div sel='.picker-controls'>
              <TagInputEl
                attrs={{
                  id: `picker`,
                  tags: this.params.tags,
                  elref: `sharingTagInput`,
                  placeholder: this.params.placeholder ?? `Enter People or Team Names...`,
                  'min-tags': 0,
                  'disable-input': this.params.disabled,
                  'mp-select-attrs': {
                    'disable-search': true,
                    sections: this.params.selectOptions,
                    'menu-width': 120,
                    'selected-option': this.params.selectedInputOption,
                    theme: SelectTheme.Tertiary,
                    'always-show-caret': true,
                    //@ts-ignore elref for testing
                    elref: `selectShareLevel`,
                    disabled: this.params.disabled,
                  },
                }}
                on={{
                  change: this.params.picker.handleInputChange,
                  focus: this.params.picker.handleInputFocus,
                  optionSelected: this.params.picker.handleLevelSelectChange,
                }}
                hook={{
                  insert: this.params.picker.handleMpTagInputInsert,
                }}
              />
              <DropMenuEl
                attrs={{
                  open: !!this.params.searchText && this.params.showPopover,
                  'prevent-close-selectors': [`mp-tag-input`],
                }}
                hook={{
                  insert: this.params.picker.handleDropMenuInsert,
                }}
                on={{
                  change: this.params.picker.handlePopoverChange,
                }}
              >
                <ItemsMenuEl
                  attrs={{
                    'search-filter': this.params.searchText,
                    'empty-message': `This list is empty`,
                    elref: `usersAndTeamsMenu`,
                  }}
                  props={{
                    sections: this.params.menuSections,
                    keydownProxyEl: this.params.itemMenuKeydownProxy,
                    sectionsFromInputFn: this.params.sectionsFromInputFn,
                  }}
                  on={{
                    clickItem: this.params.picker.handleItemClick,
                  }}
                />
              </DropMenuEl>
              <ButtonEl
                sel='.save-share'
                attrs={{
                  disabled: this.params.cta.disabled,
                  elref: `saveNewSharesButton`,
                  size: ButtonSize.Large,
                  theme: ButtonTheme.Primary,
                  ...(this.params.cta.icon ? {icon: this.params.cta.icon} : {}),
                }}
                on={{
                  click: this.params.cta.onClick,
                }}
              >
                {this.params.cta.text}
              </ButtonEl>
            </div>
          </div>
        );
      },
    };
  }
}

export const UserSearchTagInputEl = defineAndMakeJsxFactory(`mp-user-search-tag-input`, UserSearchTagInput);
export const UserSearchTagInputReact = panel2react(`mp-user-search-tag-input`, UserSearchTagInput);
